import React, { useContext, useMemo } from 'react';

import { FormContext } from '@gogame/form';

import { AFFECTED_LEVELS } from 'scenes/SmartRules/SmartRuleForm/constants';

import { CONDITION_TYPE, CONDITIONS, DYNAMIC } from './Conditions';
import { AFFECTED_LEVEL, GENERAL, SECONDARY_AFFECTED_LEVEL } from './General';

const ExtraContext = React.createContext({});

export const ExtraProvider = ({ children }) => {
  const { values, setFieldValue } = useContext(FormContext);

  const secondaryAffectedLevel = values?.[GENERAL]?.[SECONDARY_AFFECTED_LEVEL];
  const affectedLevel = values?.[GENERAL]?.[AFFECTED_LEVEL];

  const dimension = useMemo(() => {
    return secondaryAffectedLevel ? secondaryAffectedLevel : affectedLevel;
  }, [affectedLevel, secondaryAffectedLevel]);

  const isAllowDynamicConditionByAffectedLevel = useMemo(() => {
    return ![
      AFFECTED_LEVELS.REAL_COUNTRY,
      AFFECTED_LEVELS.CONCEPT,
      AFFECTED_LEVELS.CREATIVE_FILE,
      AFFECTED_LEVELS.TAG,
    ].includes(affectedLevel);
  }, [affectedLevel]);

  const isAllowDynamicConditionBySecondaryAffectedLevel = useMemo(() => {
    return ![
      AFFECTED_LEVELS.REAL_COUNTRY,
      AFFECTED_LEVELS.CONCEPT,
      AFFECTED_LEVELS.CREATIVE_FILE,
      AFFECTED_LEVELS.TAG,
    ].includes(secondaryAffectedLevel);
  }, [secondaryAffectedLevel]);

  const hasCreativesDimension = useMemo(() => {
    return (
      [
        AFFECTED_LEVELS.CONCEPT,
        AFFECTED_LEVELS.CREATIVE_FILE,
        AFFECTED_LEVELS.TAG,
      ].filter(x => [affectedLevel, secondaryAffectedLevel].includes(x))
        .length > 0
    );
  }, [affectedLevel, secondaryAffectedLevel]);

  let isAllowDynamicCondition =
    isAllowDynamicConditionBySecondaryAffectedLevel &&
    isAllowDynamicConditionByAffectedLevel;

  const hasDynamicConditions = useMemo(() => {
    return values?.[CONDITIONS]?.some(
      ({ [CONDITION_TYPE]: conditionType }) => conditionType === DYNAMIC
    );
  }, [values?.[CONDITIONS]]);

  return (
    <ExtraContext.Provider
      value={{
        dimension,
        isAllowDynamicConditionByAffectedLevel,
        isAllowDynamicConditionBySecondaryAffectedLevel,
        isAllowDynamicCondition,
        hasCreativesDimension,
        hasDynamicConditions,
        setFieldValue,
        values,
      }}
    >
      {children}
    </ExtraContext.Provider>
  );
};

export const useExtraContext = () => {
  return useContext(ExtraContext);
};
